import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Copy, Check, MessageCircle, Shield, ScanLine, Building2 } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  return `${BACKEND_URL}${src}`;
};

const CopyField = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  if (!value) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast({ title: `${label} copied`, description: value });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="min-w-0">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#1565C0]">
          {label}
        </div>
        <div
          className="mt-0.5 text-sm md:text-base font-bold text-[#0A1628] break-words"
          data-testid={`${testid}-value`}
        >
          {value}
        </div>
      </div>
      <button
        onClick={copy}
        className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-[#0A1628] text-white hover:bg-[#1565C0] transition-colors"
        data-testid={`${testid}-copy-btn`}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

const DonationQR = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/content/donation-qr`);
        setData(data);
      } catch (e) {
        console.error('donation-qr', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data || data.published === false) {
    // Keep the section anchor for header/floating buttons to scroll to
    return <section id="donate" className="py-20" data-testid="donate-section" />;
  }

  return (
    <section
      id="donate"
      className="py-24 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 relative overflow-hidden"
      data-testid="donate-section"
    >
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-amber-300/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-rose-300/30 rounded-full blur-3xl" />

      <div className="relative max-w-5xl mx-auto px-4">
        <SectionHeading
          eyebrow={data.headline || 'Support Our Mission'}
          title={(data.title || 'Scan to Donate').split(' ').slice(0, -1).join(' ') || 'Scan to'}
          accent={(data.title || 'Scan to Donate').split(' ').slice(-1)[0] || 'Donate'}
          subtitle={data.subtitle}
        />

        <Card className="border-0 shadow-2xl overflow-hidden mt-8" data-testid="donation-qr-card">
          <div className="bg-gradient-to-r from-[#1565C0] via-[#1976D2] to-[#1E88E5] p-5 md:p-6 text-white flex items-center gap-3">
            <ScanLine className="w-6 h-6 text-amber-300" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-white/70 font-semibold">
                {data.qr_label || 'DuitNow QR'}
              </div>
              <div className="text-lg md:text-xl font-black">
                Scan with any Malaysian banking app
              </div>
            </div>
            <div className="hidden md:flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-white/15 rounded-full px-3 py-1">
              <Shield className="w-3 h-3" />
              Secure
            </div>
          </div>

          <CardContent className="p-6 md:p-10">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* QR image */}
              <div className="flex flex-col items-center">
                <div className="relative w-full max-w-sm p-4 bg-white rounded-3xl shadow-xl border-4 border-[#0A1628]">
                  {data.qr_image ? (
                    <img
                      src={resolveImg(data.qr_image)}
                      alt="Donation QR code"
                      className="w-full h-auto rounded-xl"
                      data-testid="donation-qr-image"
                    />
                  ) : (
                    <div className="aspect-square flex items-center justify-center bg-gray-100 rounded-xl text-gray-400">
                      QR code not uploaded
                    </div>
                  )}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ED1C24] text-white text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-full shadow-lg">
                    {data.qr_label || 'DuitNow QR'}
                  </div>
                </div>

                <p className="mt-5 text-center text-sm text-gray-600 max-w-sm">
                  {data.description}
                </p>
              </div>

              {/* Bank / account details */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-[#1565C0]" />
                  <h3 className="text-lg font-black text-[#0A1628] uppercase tracking-wider">
                    Or transfer directly
                  </h3>
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-md p-5">
                  <CopyField label="Bank" value={data.bank_name} testid="bank-name" />
                  <CopyField label="Account Name" value={data.account_name} testid="account-name" />
                  <CopyField label="Account Number" value={data.account_number} testid="account-number" />
                </div>

                {data.reference_note && (
                  <div
                    className="mt-4 p-4 rounded-xl bg-amber-50 border-l-4 border-amber-400 text-sm text-amber-900"
                    data-testid="donation-reference-note"
                  >
                    {data.reference_note}
                  </div>
                )}

                {data.whatsapp_link && (
                  <a
                    href={data.whatsapp_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1DA851] text-white font-bold py-4 rounded-2xl shadow-lg hover:shadow-2xl transition-all uppercase tracking-wider"
                    data-testid="donation-whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp Us Your Receipt
                  </a>
                )}

                {data.tax_note && (
                  <p className="text-center text-xs text-gray-500 mt-4 flex items-center justify-center gap-1">
                    <Shield className="w-3 h-3" />
                    {data.tax_note}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default DonationQR;
